/**
 * DEX Arbitrage Bot — cross-DEX same-chain spread capture.
 *
 * Premium UX:
 *   - Live opportunity radar (rolling spread % over last 15 min)
 *   - P&L tracking with conservative estimates (cumulative net, win rate, daily count vs cap)
 *   - Loss-streak auto-pause (3 negative arbs in a row → bot pauses + notifies)
 *   - Pre-signed cycle pool (mirrors Grid Bot / TV Webhooks pattern)
 *
 * Clear risk disclosure throughout — no "guaranteed profits" language.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Zap, Activity, AlertTriangle, CheckCircle, X, Wallet, Info, Trash2, RefreshCw,
  Plus, ChevronDown, ChevronUp, PauseCircle, PlayCircle, Target, TrendingUp,
  TrendingDown, Sparkles, Radar, ShieldCheck,
} from 'lucide-react';
import {
  useAccount, useChainId, useSignTypedData,
  usePublicClient, useWalletClient, useSwitchChain,
} from 'wagmi';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { Spinner, InfoTip } from '../components/ui';
import {
  LineChart, Line, XAxis, YAxis, ReferenceLine, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  prepareDexArbBot, commitDexArbBot,
  listDexArbBots, getDexArbBot,
  pauseDexArbBot, resumeDexArbBot, cancelDexArbBot,
  scanDexArbBot, refillPrepareDexArbBot, refillCommitDexArbBot,
} from '../services/api';
import { CHAINS, PROTOCOL_CHAINS, getTokensFor } from '../constants/dexTokens';
import DexCustomTokenInput from '../components/DexCustomTokenInput';

const ERC20_ABI = [
  { name: 'allowance', type: 'function', stateMutability: 'view',
    inputs: [{ name: 'owner', type: 'address' }, { name: 'spender', type: 'address' }],
    outputs: [{ type: 'uint256' }] },
  { name: 'approve', type: 'function', stateMutability: 'nonpayable',
    inputs: [{ name: 'spender', type: 'address' }, { name: 'amount', type: 'uint256' }],
    outputs: [{ type: 'bool' }] },
];
const MAX_UINT256 = 2n ** 256n - 1n;
const COW_SPENDER = '0xC92E8bdf79f0507f65a392b0ab4667716BFE0110';

// Only chains where BOTH CoW + Velora are supported (intersection)
const ARB_CHAINS = [
  { id: 1,     label: 'Ethereum' },
  { id: 42161, label: 'Arbitrum' },
  { id: 8453,  label: 'Base' },
];

const DIRECTIONS = [
  { id: 'cow_buy_velora_sell', label: 'Buy on CoW · Sell on Velora',
    desc: 'Captures arbs when CoW solver auctions price the stable→volatile leg cheaper than Velora aggregator routing.' },
  { id: 'velora_buy_cow_sell', label: 'Buy on Velora · Sell on CoW',
    desc: 'Reverse direction — captures arbs when Velora has the cheaper stable→volatile route.' },
];

const POOL_SIZE_OPTIONS = [
  { value: 5,  label: '5 cycles',  desc: '10 sigs · small test' },
  { value: 10, label: '10 cycles', desc: '20 sigs · standard' },
  { value: 20, label: '20 cycles', desc: '40 sigs · high-frequency' },
];

function toBaseUnits(humanAmount, decimals) {
  const s = String(humanAmount).trim();
  if (!/^\d+(\.\d+)?$/.test(s)) throw new Error('amount must be a non-negative decimal');
  const [whole, frac = ''] = s.split('.');
  const padded = (frac + '0'.repeat(decimals)).slice(0, decimals);
  const combined = (whole + padded).replace(/^0+/, '') || '0';
  if (combined === '0') throw new Error('amount must be > 0');
  return combined;
}
function formatBaseUnits(baseStr, decimals, maxFrac = 4) {
  if (!baseStr || baseStr === '0') return '0';
  const s = String(baseStr).padStart(decimals + 1, '0');
  const whole = s.slice(0, -decimals).replace(/^0+/, '') || '0';
  const frac  = s.slice(-decimals).slice(0, maxFrac).replace(/0+$/, '');
  return frac ? `${whole}.${frac}` : whole;
}
function fmtTime(iso) { try { return new Date(iso).toLocaleString(); } catch { return iso || '—'; } }
function fmtTimeShort(iso) { try { return new Date(iso).toLocaleTimeString(); } catch { return '—'; } }
function fmtUsd(n, frac = 2) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `${v < 0 ? '-' : ''}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: frac, maximumFractionDigits: frac })}`;
}
function copyToClipboard(text) { try { navigator.clipboard?.writeText(text); } catch {} }

function Toast({ message, type = 'success', onDismiss }) {
  useEffect(() => { const t = setTimeout(onDismiss, 6000); return () => clearTimeout(t); }, [onDismiss]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-2xl text-sm font-medium max-w-md
      ${type === 'success' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
        : type === 'error' ? 'bg-red-500/20 text-red-300 border border-red-500/30'
        : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'}`}>
      {message}
    </div>
  );
}
function Card({ children, className = '' }) {
  return <div className={`bg-dark-800 rounded-2xl p-6 border border-dark-700/50 ${className}`}>{children}</div>;
}

// ─── Opportunity radar — live spread chart ─────────────────────────────────
// Polls /scan every 60 seconds, accumulates data points in client memory.
// Renders a small line chart of (timestamp, spread%) with a threshold reference.
function OpportunityRadar({ arbBotId, threshold, paused }) {
  const [history, setHistory] = useState([]); // [{ t: timestamp, spread: pct, wouldFire: bool }]
  const [scanning, setScanning] = useState(false);
  const [lastError, setLastError] = useState(null);
  const intervalRef = useRef(null);

  const doScan = useCallback(async () => {
    if (paused) return;
    setScanning(true);
    try {
      const r = await scanDexArbBot(arbBotId);
      const d = r.data || {};
      const point = {
        t: new Date(d.scannedAt || Date.now()).getTime(),
        spread: d.spread?.spreadPct ?? null,
        wouldFire: !!d.wouldFire,
        error: d.scanError || null,
      };
      setHistory(h => {
        const next = [...h, point];
        // Keep last 30 points = 30 minutes at 60s intervals
        return next.length > 30 ? next.slice(-30) : next;
      });
      setLastError(d.scanError || null);
    } catch (err) {
      setLastError(err?.message || 'scan failed');
    } finally { setScanning(false); }
  }, [arbBotId, paused]);

  useEffect(() => {
    doScan(); // immediate
    intervalRef.current = setInterval(doScan, 60_000);
    return () => clearInterval(intervalRef.current);
  }, [doScan]);

  const chartData = history.map((p, i) => ({
    idx: i,
    label: new Date(p.t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    spread: p.spread != null ? Number(p.spread.toFixed(4)) : null,
    wouldFire: p.wouldFire,
  }));
  const last = history[history.length - 1];

  return (
    <div className="bg-gradient-to-br from-dark-700/50 to-dark-700/10 rounded-xl border border-dark-600 p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Radar className={`w-3.5 h-3.5 ${scanning ? 'text-accent-primary animate-pulse' : 'text-emerald-400'}`} />
          <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Live opportunity radar</span>
          <span className="text-[10px] text-gray-500">polling every 60s</span>
        </div>
        <button onClick={doScan} disabled={scanning}
          className="text-[10px] px-2 py-0.5 rounded bg-dark-700 hover:bg-dark-600 text-accent-primary disabled:opacity-50 flex items-center gap-1">
          <RefreshCw className={`w-3 h-3 ${scanning ? 'animate-spin' : ''}`} /> Scan now
        </button>
      </div>
      {chartData.length === 0 ? (
        <div className="h-[150px] flex flex-col items-center justify-center text-gray-500 text-xs gap-1.5">
          {paused ? (
            <>
              <PauseCircle className="w-6 h-6 text-amber-400" />
              <span className="text-amber-300 font-semibold">Bot is paused</span>
              <span className="text-[10px] text-gray-600">Activate (▶) to start radar polling</span>
            </>
          ) : (
            <>
              <Radar className="w-5 h-5 text-accent-primary animate-pulse" />
              <span>Waiting for first scan…</span>
              <span className="text-[10px] text-gray-600">First data point arrives within 60s</span>
            </>
          )}
        </div>
      ) : (
        <div className="h-[150px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <XAxis dataKey="label" stroke="#6b7280" fontSize={10} interval="preserveStartEnd" />
              <YAxis stroke="#6b7280" fontSize={10} tickFormatter={(v) => `${v.toFixed(2)}%`} domain={['auto', 'auto']} />
              <Tooltip contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 8 }}
                labelStyle={{ color: '#9ca3af' }}
                formatter={(v) => [`${Number(v).toFixed(3)}%`, 'Spread']} />
              <ReferenceLine y={threshold} stroke="#10b981" strokeDasharray="3 3"
                label={{ value: `Threshold ${threshold}%`, fill: '#10b981', fontSize: 9, position: 'right' }} />
              <Line type="monotone" dataKey="spread" stroke="#f59e0b" strokeWidth={1.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="flex items-center justify-between text-[11px] mt-1">
        <span className="text-gray-400">
          Current spread: <span className={last?.spread > threshold ? 'text-emerald-400 font-semibold' : 'text-white font-mono'}>
            {last?.spread != null ? `${last.spread.toFixed(3)}%` : '—'}
          </span>
          {last?.wouldFire && <span className="ml-2 text-emerald-400 font-semibold">⚡ ready to fire</span>}
        </span>
        {lastError && (
          <span className="text-amber-400 truncate max-w-[60%]" title={lastError}>⚠ {lastError.slice(0, 50)}…</span>
        )}
      </div>
    </div>
  );
}

// ─── P&L summary ────────────────────────────────────────────────────────────
function PnlSummary({ bot }) {
  const total = bot.firesCount || 0;
  const wins  = bot.profitableFiresCount || 0;
  const winRate = total > 0 ? (wins / total) * 100 : 0;
  const todayCount = (bot.firesLog || []).filter(f => {
    const t = Date.parse(f.firedAt || 0);
    return Number.isFinite(t) && (Date.now() - t) < 24 * 3600 * 1000;
  }).length;
  const netUsd = Number(bot.cumulativeNetUsd || 0);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      <div className="bg-dark-700/40 rounded-lg px-3 py-2">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-0.5">Cumulative net</div>
        <div className={`text-sm font-mono font-semibold ${netUsd > 0 ? 'text-emerald-400' : netUsd < 0 ? 'text-red-400' : 'text-white'}`}>
          {fmtUsd(netUsd, 4)}
        </div>
      </div>
      <div className="bg-dark-700/40 rounded-lg px-3 py-2">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-0.5">Fires</div>
        <div className="text-sm font-mono text-white">{wins} / {total} <span className="text-[10px] text-gray-500">profitable</span></div>
      </div>
      <div className="bg-dark-700/40 rounded-lg px-3 py-2">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-0.5">Win rate</div>
        <div className={`text-sm font-mono ${winRate >= 50 ? 'text-emerald-400' : winRate > 0 ? 'text-amber-300' : 'text-gray-400'}`}>
          {total > 0 ? `${winRate.toFixed(0)}%` : '—'}
        </div>
      </div>
      <div className="bg-dark-700/40 rounded-lg px-3 py-2">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-0.5">Today's fires</div>
        <div className="text-sm font-mono text-white">{todayCount} / {bot.dailyCap}<span className="text-[10px] text-gray-500"> cap</span></div>
      </div>
    </div>
  );
}

// ─── Arb bot row ────────────────────────────────────────────────────────────
function ArbBotRow({ bot, onDelete, onPause, onResume, onRefill, busy }) {
  const [expanded, setExpanded] = useState(false);
  const buyProto  = bot.direction === 'cow_buy_velora_sell' ? 'CoW' : 'Velora';
  const sellProto = bot.direction === 'cow_buy_velora_sell' ? 'Velora' : 'CoW';
  const poolRemaining = (bot.pool || []).filter(c => !c.used).length;
  const poolTotal = (bot.pool || []).length;
  const pair = bot.pair || {};

  const statusCls = {
    armed:     'bg-emerald-500/10 text-emerald-400 border-emerald-500/30 animate-[arb-pulse_2s_ease-in-out_infinite]',
    paused:    'bg-amber-500/10 text-amber-300 border-amber-500/30',
    cancelled: 'bg-dark-600 text-gray-400 border-dark-500',
    completed: 'bg-blue-500/10 text-blue-300 border-blue-500/30',
    failed:    'bg-red-500/10 text-red-400 border-red-500/30',
  }[bot.status] || 'bg-dark-600 text-gray-400 border-dark-500';

  return (
    <div className="bg-dark-700/40 rounded-xl border border-dark-600/40 overflow-hidden hover:border-dark-500/60 transition-colors">
      <button onClick={() => setExpanded(e => !e)}
        className="w-full px-4 py-3 text-left transition-colors hover:bg-dark-600/20">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Zap className="w-3.5 h-3.5 text-accent-primary" />
              <span className="text-white text-sm font-semibold truncate">{bot.name}</span>
              <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium border ${statusCls}`}>
                {bot.status === 'armed' ? 'Watching' : bot.status.charAt(0).toUpperCase() + bot.status.slice(1)}
              </span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-dark-600 text-gray-400 border border-dark-500">
                {pair.sellSymbol}↔{pair.buySymbol}
              </span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-dark-600 text-gray-300 border border-dark-500">
                {buyProto} → {sellProto}
              </span>
              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/5 text-emerald-300 border border-emerald-500/20">
                <ShieldCheck className="w-2.5 h-2.5" /> MEV-protected
              </span>
            </div>
            <div className="flex items-center gap-3 flex-wrap mt-1 text-xs">
              <span><span className="text-gray-500">Threshold:</span> <span className="text-white font-mono">≥{bot.minSpreadPct}%</span></span>
              <span><span className="text-gray-500">Per cycle:</span> <span className="text-white">{formatBaseUnits(bot.perCycleSellAmountBase, pair.sellDecimals)} {pair.sellSymbol}</span></span>
              <span><span className="text-gray-500">Pool:</span> <span className={poolRemaining < 3 ? 'text-amber-300 font-semibold' : 'text-emerald-300'}>{poolRemaining}/{poolTotal}</span></span>
              <span><span className="text-gray-500">Net:</span> <span className={Number(bot.cumulativeNetUsd) > 0 ? 'text-emerald-400 font-semibold' : Number(bot.cumulativeNetUsd) < 0 ? 'text-red-400 font-semibold' : 'text-gray-300'}>{fmtUsd(bot.cumulativeNetUsd, 4)}</span></span>
            </div>
            {bot.pausedReason && bot.status === 'paused' && (
              <p className="text-amber-300 text-[11px] mt-1">⚠ Paused: {bot.pausedReason.startsWith('loss_streak') ? `${bot.pausedReason.split('_').pop()} losing arbs in a row — auto-pause for safety` : bot.pausedReason}</p>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
            {bot.status === 'armed' && (
              <button onClick={() => onPause(bot.arbBotId)} disabled={busy} title="Pause"
                className="p-1.5 rounded-lg bg-dark-700 text-amber-400 hover:bg-amber-500/10 disabled:opacity-50">
                <PauseCircle className="w-3.5 h-3.5" />
              </button>
            )}
            {bot.status === 'paused' && (
              <button onClick={() => onResume(bot.arbBotId)} disabled={busy} title="Resume"
                className="p-1.5 rounded-lg bg-dark-700 text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-50">
                <PlayCircle className="w-3.5 h-3.5" />
              </button>
            )}
            <button onClick={() => onRefill(bot.arbBotId)} disabled={busy} title="Refill cycles"
              className="p-1.5 rounded-lg bg-dark-700 text-accent-primary hover:bg-accent-primary/10 disabled:opacity-50">
              <Plus className="w-3.5 h-3.5" />
            </button>
            {bot.status !== 'cancelled' && bot.status !== 'completed' && (
              <button onClick={() => onDelete(bot.arbBotId)} disabled={busy} title="Cancel"
                className="p-1.5 rounded-lg bg-dark-700 text-red-400 hover:bg-red-500/10 disabled:opacity-50">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
            <span className="ml-1 text-gray-500">
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </span>
          </div>
        </div>
      </button>

      {expanded && (
        <div className="px-4 py-3 border-t border-dark-600/40 bg-dark-800/40 space-y-3 animate-[arb-fade_0.2s_ease-out]">
          <PnlSummary bot={bot} />
          <OpportunityRadar arbBotId={bot.arbBotId} threshold={bot.minSpreadPct} paused={bot.status !== 'armed'} />

          {/* Fires log */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5">Recent fires ({bot.firesLog?.length || 0})</div>
            {Array.isArray(bot.firesLog) && bot.firesLog.length > 0 ? (
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {bot.firesLog.map(f => {
                  const isWin = Number(f.netUsd) > 0;
                  const statusCls = f.status === 'submitted' ? 'text-emerald-400'
                                  : f.status === 'partial' ? 'text-amber-300'
                                  : f.status === 'submit_failed' ? 'text-red-400'
                                  : 'text-gray-400';
                  return (
                    <div key={f.id} className="flex items-center justify-between text-xs px-3 py-1.5 bg-dark-700/40 rounded-lg">
                      <span className="flex items-center gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full ${isWin ? 'bg-emerald-400' : 'bg-red-400'}`} />
                        <span className={statusCls}>{f.status === 'submitted' ? '✓' : f.status === 'partial' ? '◐' : f.status === 'submit_failed' ? '✗' : '·'}</span>
                        <span className="text-gray-300">spread {Number(f.spreadDetected || 0).toFixed(2)}%</span>
                        <span className={isWin ? 'text-emerald-400 font-mono' : 'text-red-400 font-mono'}>
                          {fmtUsd(f.netUsd, 4)}
                        </span>
                      </span>
                      <span className="text-gray-500 text-[10px]">{fmtTimeShort(f.firedAt)}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-dark-700/30 border border-dashed border-dark-600 rounded-lg px-3 py-4 text-center">
                <p className="text-gray-500 text-xs">No fires yet — watching for spreads to cross {bot.minSpreadPct}%.</p>
              </div>
            )}
          </div>

          {/* Meta */}
          <div className="bg-dark-700/40 rounded-lg px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Arb Bot ID</div>
            <button onClick={() => copyToClipboard(bot.arbBotId)} title="Copy"
              className="text-xs font-mono text-white break-all hover:text-accent-primary transition-colors text-left">
              {bot.arbBotId}
            </button>
            <div className="grid grid-cols-2 gap-2 text-xs mt-2 pt-2 border-t border-dark-600/40">
              <div><span className="text-gray-500">Created:</span> <span className="text-gray-300">{fmtTime(bot.createdAt)}</span></div>
              <div><span className="text-gray-500">Orders valid until:</span> <span className="text-gray-300">{fmtTime(new Date((bot.ordersValidTo || 0) * 1000).toISOString())}</span></div>
              <div><span className="text-gray-500">Chain:</span> <span className="text-gray-300">{bot.chainId}</span></div>
              <div><span className="text-gray-500">Direction:</span> <span className="text-gray-300">{buyProto} → {sellProto}</span></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Create panel ───────────────────────────────────────────────────────────
function CreateArbBotPanel({ onCreated, onToast }) {
  const { address: connectedAddr, isConnected } = useAccount();
  const connectedChainId = useChainId();
  const { signTypedDataAsync } = useSignTypedData();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const { switchChain, isPending: switchingChain } = useSwitchChain();

  const [name, setName] = useState('');
  const [chainId, setChainId] = useState(connectedChainId || 8453);
  const [sellSym, setSellSym] = useState('USDC');
  const [buySym, setBuySym] = useState('WETH');
  const [perCycleAmount, setPerCycleAmount] = useState('');
  const [minSpreadPct, setMinSpreadPct] = useState(0.30);
  const [poolSize, setPoolSize] = useState(5);
  const [dailyCap, setDailyCap] = useState(10);
  const [direction, setDirection] = useState('cow_buy_velora_sell');
  const [wizard, setWizard] = useState(null);
  const [customTick, setCustomTick] = useState(0);

  const tokens = useMemo(() => getTokensFor(chainId), [chainId, customTick]);
  const sellToken = tokens.find(t => t.symbol === sellSym);
  const buyToken  = tokens.find(t => t.symbol === buySym);
  const chainsForBoth = ARB_CHAINS.filter(c =>
    PROTOCOL_CHAINS.cow.includes(c.id) && PROTOCOL_CHAINS.velora.includes(c.id)
  );

  useEffect(() => {
    const list = getTokensFor(chainId);
    if (!list.find(t => t.symbol === sellSym)) setSellSym(list[0]?.symbol || '');
    if (!list.find(t => t.symbol === buySym))  setBuySym(list[1]?.symbol || list[0]?.symbol || '');
    if (sellSym && buySym && sellSym === buySym) {
      const alt = list.find(t => t.symbol !== sellSym);
      if (alt) setBuySym(alt.symbol);
    }
  }, [chainId, sellSym, buySym]);

  const disabledReason = (() => {
    if (!isConnected || !connectedAddr) return 'Connect your wallet first';
    if (Number(connectedChainId) !== Number(chainId))
      return `Wallet is on chain ${connectedChainId}, not ${ARB_CHAINS.find(c => c.id === Number(chainId))?.label || chainId}`;
    if (!sellToken || !buyToken || sellToken.symbol === buyToken.symbol) return 'Sell and buy tokens must be different';
    if (!(Number(perCycleAmount) > 0)) return 'Enter a per-cycle amount > 0';
    if (wizard) return 'Submitting…';
    return null;
  })();
  const canSubmit = disabledReason == null;

  async function ensureAllowance(spender, requiredAmount) {
    setWizard(w => ({ ...w, phase: 'checking-allowance' }));
    async function read() {
      return publicClient.readContract({
        address: sellToken.address, abi: ERC20_ABI,
        functionName: 'allowance', args: [connectedAddr, spender],
      });
    }
    let allowance = await read();
    if (allowance >= requiredAmount) return;
    if (allowance > 0n) {
      setWizard(w => ({ ...w, phase: 'approving' }));
      onToast({ type: 'info', message: `${sellSym} needs allowance reset to 0 first — confirm in wallet.` });
      await walletClient.writeContract({
        address: sellToken.address, abi: ERC20_ABI,
        functionName: 'approve', args: [spender, 0n],
      });
      const start = Date.now();
      while (Date.now() - start < 180_000) {
        await new Promise(r => setTimeout(r, 4000));
        try { allowance = await read(); } catch {}
        if (allowance === 0n) break;
      }
      if (allowance !== 0n) throw new Error('Allowance reset did not confirm');
    }
    setWizard(w => ({ ...w, phase: 'approving' }));
    onToast({ type: 'info', message: `Approve ${sellSym} once. ~15-30s for on-chain confirmation.` });
    await walletClient.writeContract({
      address: sellToken.address, abi: ERC20_ABI,
      functionName: 'approve', args: [spender, MAX_UINT256],
    });
    const start = Date.now();
    while (Date.now() - start < 180_000) {
      await new Promise(r => setTimeout(r, 4000));
      try { allowance = await read(); } catch {}
      if (allowance >= requiredAmount) return;
    }
    throw new Error('Approval did not confirm in 3 min');
  }

  async function handleSubmit() {
    if (!canSubmit) return;
    try {
      const perCycleBase = toBaseUnits(perCycleAmount, sellToken.decimals);
      // Total allowance needed = perCycleBase × poolSize (one cycle's worth per fire)
      const totalSellNeeded = BigInt(perCycleBase) * BigInt(poolSize);

      setWizard({ phase: 'preparing' });
      const prep = await prepareDexArbBot({
        chainId, owner: connectedAddr, name,
        direction,
        sellToken: sellToken.address, buyToken: buyToken.address,
        sellSymbol: sellToken.symbol, buySymbol: buyToken.symbol,
        sellDecimals: sellToken.decimals, buyDecimals: buyToken.decimals,
        perCycleSellAmountBase: perCycleBase,
        minSpreadPct: Number(minSpreadPct),
        poolSize: Number(poolSize),
        dailyCap: Number(dailyCap),
      });

      const { arbBotId, cycles, referenceQuotes, cycleEconomics, validTo, buySpender } = prep.data;

      const sigsTotal = cycles.length * 2;
      setWizard({ phase: 'signing', signedSoFar: 0, sigsTotal });

      // Approve the buy-side spender (CoW or Velora) — that's where stable gets pulled
      const spender = buySpender || COW_SPENDER;
      await ensureAllowance(spender, totalSellNeeded);

      // Sign each cycle's two legs sequentially
      const signedCycles = [];
      let done = 0;
      for (const c of cycles) {
        setWizard(w => ({ ...w, phase: 'signing-buy', signedSoFar: done }));
        const buySig = await signTypedDataAsync({
          domain: c.buyLeg.typedData.domain, types: c.buyLeg.typedData.types,
          primaryType: c.buyLeg.typedData.primaryType, message: c.buyLeg.order,
        });
        done++; setWizard(w => ({ ...w, signedSoFar: done }));
        setWizard(w => ({ ...w, phase: 'signing-sell', signedSoFar: done }));
        const sellSig = await signTypedDataAsync({
          domain: c.sellLeg.typedData.domain, types: c.sellLeg.typedData.types,
          primaryType: c.sellLeg.typedData.primaryType, message: c.sellLeg.order,
        });
        done++; setWizard(w => ({ ...w, signedSoFar: done }));

        signedCycles.push({
          slotIdx: c.slotIdx,
          buyLeg:  { order: c.buyLeg.order,  signature: buySig,  orderId: c.buyLeg.orderId,  sellAmount: c.buyLeg.sellAmount,  buyAmount: c.buyLeg.buyAmount },
          sellLeg: { order: c.sellLeg.order, signature: sellSig, orderId: c.sellLeg.orderId, sellAmount: c.sellLeg.sellAmount, buyAmount: c.sellLeg.buyAmount },
        });
      }

      setWizard(w => ({ ...w, phase: 'committing' }));
      await commitDexArbBot({
        arbBotId, chainId, owner: connectedAddr, name, direction,
        sellToken: sellToken.address, buyToken: buyToken.address,
        sellSymbol: sellToken.symbol, buySymbol: buyToken.symbol,
        sellDecimals: sellToken.decimals, buyDecimals: buyToken.decimals,
        perCycleSellAmountBase: perCycleBase,
        minSpreadPct: Number(minSpreadPct),
        poolSize: Number(poolSize),
        dailyCap: Number(dailyCap),
        referenceQuotes, cycleEconomics, validTo,
        cycles: signedCycles,
      });

      onToast({ type: 'success', message: `Arb Bot armed — ${cycles.length} cycles ready. Activate when you're ready to start watching.` });
      setName(''); setPerCycleAmount('');
      onCreated();
      setWizard(null);
    } catch (err) {
      onToast({ type: 'error', message: err?.shortMessage || err?.message || 'Could not arm arb bot' });
      setWizard(null);
    }
  }

  const stageLabel = (() => {
    if (!wizard) return 'Arm Arb Bot';
    if (wizard.phase === 'preparing') return 'Quoting both sides + building cycles…';
    if (wizard.phase === 'checking-allowance') return 'Checking allowance…';
    if (wizard.phase === 'approving') return `Approving ${sellSym}…`;
    if (wizard.phase?.startsWith('signing')) return `Sign ${(wizard.signedSoFar || 0) + 1} of ${wizard.sigsTotal}…`;
    if (wizard.phase === 'committing') return 'Saving bot…';
    return 'Working…';
  })();

  return (
    <Card>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-accent-primary/20 to-accent-primary/5 text-accent-primary flex items-center justify-center border border-accent-primary/20">
          <Zap className="w-4 h-4" />
        </div>
        <div>
          <h3 className="text-white font-semibold text-sm">New Arbitrage Bot</h3>
          <p className="text-gray-500 text-xs">Captures cross-DEX spreads between CoW and Velora solver networks. Pre-signed pool · MEV-protected · auto-pauses on loss streaks.</p>
        </div>
      </div>

      {!isConnected && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl px-4 py-3 mb-4 space-y-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-amber-300 text-sm font-semibold">Connect your wallet first</p>
          </div>
          <ConnectButton.Custom>
            {({ openConnectModal }) => (
              <button onClick={openConnectModal}
                className="w-full py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-400 hover:to-amber-500 flex items-center justify-center gap-2">
                <Wallet className="w-4 h-4" /> Connect Wallet
              </button>
            )}
          </ConnectButton.Custom>
        </div>
      )}
      {isConnected && connectedAddr && (
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl px-4 py-2 mb-4 flex items-center gap-2 text-xs">
          <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-emerald-300">Connected as <code className="font-mono">{connectedAddr.slice(0,6)}…{connectedAddr.slice(-4)}</code></span>
        </div>
      )}
      {isConnected && Number(connectedChainId) !== Number(chainId) && (
        <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl px-4 py-3 mb-4 space-y-3">
          <div className="flex items-start gap-3">
            <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
            <p className="text-blue-300 text-xs">Wallet is on chain {connectedChainId}; switch to {ARB_CHAINS.find(c => c.id === chainId)?.label}.</p>
          </div>
          <button onClick={() => switchChain?.({ chainId: Number(chainId) })} disabled={switchingChain || !switchChain}
            className="w-full py-2.5 rounded-xl text-sm font-bold bg-blue-500 hover:bg-blue-400 text-white disabled:opacity-40">
            {switchingChain ? 'Switching…' : `Switch to ${ARB_CHAINS.find(c => c.id === Number(chainId))?.label}`}
          </button>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 block">Name <span className="text-gray-600 normal-case">(optional)</span></label>
          <input value={name} onChange={e => setName(e.target.value)} maxLength={80}
            placeholder="e.g. ETH cross-DEX hunter"
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary placeholder-gray-600" />
        </div>

        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 block">Network</label>
          <select value={chainId} onChange={e => setChainId(Number(e.target.value))}
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary">
            {chainsForBoth.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <p className="text-[10px] text-gray-500 mt-1">Only chains where BOTH CoW and Velora are live are shown.</p>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 block">Stable</label>
            <select value={sellSym} onChange={e => setSellSym(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary">
              {tokens.map(t => <option key={t.address} value={t.symbol}>{t.symbol}</option>)}
            </select>
          </div>
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 block">Per cycle</label>
            <input type="number" min="0" step="any" value={perCycleAmount}
              onChange={e => setPerCycleAmount(e.target.value)} placeholder="100"
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary placeholder-gray-600" />
          </div>
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 block">Volatile</label>
            <select value={buySym} onChange={e => setBuySym(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary">
              {tokens.filter(t => t.symbol !== sellSym).map(t => <option key={t.address} value={t.symbol}>{t.symbol}</option>)}
            </select>
          </div>
        </div>

        <DexCustomTokenInput chainId={chainId}
          onResolved={(tok) => { setCustomTick(t => t + 1); setBuySym(tok.symbol); }} />

        {/* Direction */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 flex items-center gap-1">
            Arb direction
            <InfoTip side="right">Each bot watches ONE direction. Run both directions (two separate bots) to capture spreads in both ways — one of them will usually be the winner at any given time.</InfoTip>
          </label>
          <div className="grid grid-cols-1 gap-2">
            {DIRECTIONS.map(d => (
              <button key={d.id} type="button" onClick={() => setDirection(d.id)}
                className={`text-left px-3 py-2 rounded-xl border transition-all ${
                  direction === d.id ? 'bg-accent-primary/10 border-accent-primary/40 text-white' : 'bg-dark-700 border-dark-600 text-gray-400 hover:bg-dark-600'}`}>
                <div className="text-xs font-semibold">{d.label}</div>
                <div className="text-[10px] text-gray-500 mt-0.5 leading-tight">{d.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Threshold + daily cap */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 flex items-center gap-1">
              Min spread %
              <InfoTip side="top">Net spread needed before firing. 0.30% covers typical fees. Below 0.20% almost always loses. Above 0.50% = rare fires, mostly during volatility spikes.</InfoTip>
            </label>
            <input type="number" min="0.1" max="5" step="0.05" value={minSpreadPct}
              onChange={e => setMinSpreadPct(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary" />
            <p className="text-[10px] text-gray-500 mt-1">Net spread after fees + slippage. 0.30% is a typical floor.</p>
          </div>
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 flex items-center gap-1">
              Daily fire cap
              <InfoTip side="top">Blast-radius limit. Set to (max-tolerable-loss / per-cycle-amount). 3-loss streak also triggers auto-pause regardless of cap.</InfoTip>
            </label>
            <input type="number" min="1" max="50" step="1" value={dailyCap}
              onChange={e => setDailyCap(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary" />
            <p className="text-[10px] text-gray-500 mt-1">Max arbs per 24h. Prevents dust drain.</p>
          </div>
        </div>

        {/* Pool size */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 flex items-center gap-1">
            Cycle pool size
            <InfoTip side="right">Each cycle = 2 EIP-712 popups (buy leg + sell leg). 5 cycles = 10 popups. Pick 5 for first test, scale up only after validating fires + win rate.</InfoTip>
          </label>
          <div className="grid grid-cols-3 gap-2">
            {POOL_SIZE_OPTIONS.map(o => (
              <button key={o.value} type="button" onClick={() => setPoolSize(o.value)}
                className={`text-left px-3 py-2 rounded-xl border transition-all ${
                  poolSize === o.value ? 'bg-accent-primary/10 border-accent-primary/40 text-white' : 'bg-dark-700 border-dark-600 text-gray-400 hover:bg-dark-600'}`}>
                <div className="text-xs font-semibold">{o.label}</div>
                <div className="text-[10px] text-gray-500 mt-0.5">{o.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* How it works + risk disclosure */}
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl px-3 py-2 text-[11px] text-amber-200">
          <p className="leading-relaxed">
            <strong className="font-semibold">How it works:</strong> the bot targets solver-network spreads that aren't worth MEV searchers' gas budget. Profits typically range 0.1–0.5% per arb when fires occur. Loss-streak auto-pause (3 negatives in a row) protects against runaway losses. Past spreads don't guarantee future ones.
          </p>
        </div>

        <button onClick={handleSubmit} disabled={!canSubmit}
          className={`relative w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-all overflow-hidden
            ${canSubmit
              ? 'bg-gradient-to-r from-accent-primary to-amber-500 text-dark-900 shadow-lg shadow-accent-primary/20 hover:shadow-xl hover:shadow-accent-primary/30 hover:-translate-y-px'
              : 'bg-dark-700 text-gray-500'}`}>
          {wizard ? <Spinner size="sm" /> : <Zap className="w-4 h-4" />}
          {stageLabel}
        </button>
        {disabledReason ? (
          <p className="text-amber-400 text-[11px] text-center flex items-center justify-center gap-1">
            <AlertTriangle className="w-3 h-3" /> {disabledReason}
          </p>
        ) : (
          <p className="text-gray-500 text-[11px] text-center">
            One {sellSym} approval + {poolSize * 2} signatures. After that, the worker watches every minute.
          </p>
        )}
      </div>
    </Card>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────
export default function DexArbitragePage() {
  const { signTypedDataAsync } = useSignTypedData();
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try { const r = await listDexArbBots(); setBots(r.data?.bots || []); }
    catch (err) { setToast({ type: 'error', message: err?.message || 'Could not load arb bots' }); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const handlePause = async (id) => {
    setBusy(id);
    try { await pauseDexArbBot(id); await reload(); setToast({ type: 'success', message: 'Arb Bot paused. Worker stops firing until resumed.' }); }
    catch (err) { setToast({ type: 'error', message: err?.message || 'Pause failed' }); }
    finally { setBusy(null); }
  };
  const handleResume = async (id) => {
    setBusy(id);
    try { await resumeDexArbBot(id); await reload(); setToast({ type: 'success', message: 'Arb Bot resumed.' }); }
    catch (err) { setToast({ type: 'error', message: err?.message || 'Resume failed' }); }
    finally { setBusy(null); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Cancel this arb bot? Unused cycles will be abandoned (signed orders left on the orderbook expire at their validTo).')) return;
    setBusy(id);
    try { await cancelDexArbBot(id); await reload(); setToast({ type: 'success', message: 'Arb Bot cancelled.' }); }
    catch (err) { setToast({ type: 'error', message: err?.message || 'Cancel failed' }); }
    finally { setBusy(null); }
  };
  const handleRefill = async (id) => {
    setBusy(id);
    try {
      const prep = await refillPrepareDexArbBot(id);
      const { cycles, referenceQuotes, cycleEconomics, validTo } = prep.data;
      if (!cycles?.length) throw new Error('Nothing to sign');
      const signed = [];
      for (const c of cycles) {
        const buySig = await signTypedDataAsync({
          domain: c.buyLeg.typedData.domain, types: c.buyLeg.typedData.types,
          primaryType: c.buyLeg.typedData.primaryType, message: c.buyLeg.order,
        });
        const sellSig = await signTypedDataAsync({
          domain: c.sellLeg.typedData.domain, types: c.sellLeg.typedData.types,
          primaryType: c.sellLeg.typedData.primaryType, message: c.sellLeg.order,
        });
        signed.push({
          slotIdx: c.slotIdx,
          buyLeg:  { order: c.buyLeg.order,  signature: buySig,  orderId: c.buyLeg.orderId,  sellAmount: c.buyLeg.sellAmount,  buyAmount: c.buyLeg.buyAmount },
          sellLeg: { order: c.sellLeg.order, signature: sellSig, orderId: c.sellLeg.orderId, sellAmount: c.sellLeg.sellAmount, buyAmount: c.sellLeg.buyAmount },
        });
      }
      await refillCommitDexArbBot(id, { cycles: signed, referenceQuotes, cycleEconomics, validTo });
      await reload();
      setToast({ type: 'success', message: `Added ${signed.length} cycles to the arb pool.` });
    } catch (err) { setToast({ type: 'error', message: err?.message || 'Refill failed' }); }
    finally { setBusy(null); }
  };

  const active = bots.filter(b => b.status === 'armed' || b.status === 'paused');
  const inactive = bots.filter(b => !['armed', 'paused'].includes(b.status));

  return (
    <div className="space-y-6">
      <style>{`
        @keyframes arb-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.65; } }
        @keyframes arb-fade { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-primary/25 to-accent-primary/5 text-accent-primary flex items-center justify-center border border-accent-primary/20">
            <Zap className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">DEX Arbitrage Bot</h1>
            <p className="text-gray-400 text-sm">Captures cross-DEX spreads between CoW and Velora solver networks. Pre-signed cycles · MEV-protected fills · auto-pauses on loss streaks for safety.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CreateArbBotPanel onCreated={reload} onToast={setToast} />

        <Card>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-white font-semibold text-sm">Active arb bots</h3>
              <p className="text-gray-500 text-xs">{active.length} configured</p>
            </div>
            <button onClick={reload} disabled={loading}
              className="btn-ghost text-xs px-3 py-1.5 flex items-center gap-1.5">
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Reload
            </button>
          </div>
          {loading ? (
            <div className="flex justify-center py-10"><Spinner /></div>
          ) : active.length === 0 ? (
            <div className="text-center py-10">
              <Radar className="w-8 h-8 text-gray-600 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">No arb bots yet.</p>
              <p className="text-gray-600 text-[11px] mt-1">Configure one → activate → watch the live radar pick up opportunities.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {active.map(b => (
                <ArbBotRow key={b.arbBotId} bot={b}
                  onDelete={handleDelete} onPause={handlePause}
                  onResume={handleResume} onRefill={handleRefill}
                  busy={busy === b.arbBotId} />
              ))}
            </div>
          )}
        </Card>
      </div>

      {inactive.length > 0 && (
        <Card>
          <h3 className="text-white font-semibold text-sm mb-3">Inactive ({inactive.length})</h3>
          <div className="space-y-2">
            {inactive.map(b => (
              <ArbBotRow key={b.arbBotId} bot={b}
                onDelete={handleDelete} onPause={handlePause}
                onResume={handleResume} onRefill={handleRefill}
                busy={busy === b.arbBotId} />
            ))}
          </div>
        </Card>
      )}

      {toast && <Toast {...toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}
